import * as React from "react";
import { useEffect, useRef } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { RowSelectionState } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../shadcn/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shadcn/dropdown-menu";

export type BulkActionMenuItem<Data extends object> = {
  name: string;
  description?: string;
  onClick: (selectedRows: Data[]) => void;
  onClickAll?: () => void;
};

export type BulkActionConfirmation = {
  title: string;
  description: React.ReactNode;
  /** Caveat rendered as a NoxusAlert below the description. */
  note?: React.ReactNode;
  confirmText?: string;
};

export type BulkAction<Data extends object> = {
  icon: React.ComponentType<{
    size?: string | number;
    color?: string;
    className?: string;
  }>;
  name: string;
  color?: string;
  onClick?: (selectedRows: Data[]) => void;
  onClickAll?: () => void;
  // When present, the action renders as a dropdown (opens upward) instead of a
  // direct button — used to group related exports under one "Export" button.
  items?: BulkActionMenuItem<Data>[];
  isLoading?: boolean;
  isHidden?: (selectedRows: Data[]) => boolean;
  // Confirmation shown before the action runs, whether it was fired on a row
  // selection or on the whole filtered set. `count` is how many rows it will
  // affect, so the copy can name the number; `wholeSet` says which path fired,
  // for an action that only needs the table's dialog on one of them. Returning
  // null declines it — for a row path that opens a dialog of its own.
  confirm?: (count: number, wholeSet: boolean) => BulkActionConfirmation | null;
};

export type PendingBulkAction = BulkActionConfirmation & {
  confirmText: string;
  destructive: boolean;
  run: () => void;
};

export function BulkActionButtons<Data extends object>({
  bulkActions,
  selectedRows,
  isAllSelected = false,
  selectAllCount,
  resourceLabel = "item",
  setPendingAction,
  setRowSelection,
  setIsAllSelected,
}: {
  bulkActions: BulkAction<Data>[];
  selectedRows: Data[];
  isAllSelected?: boolean;
  selectAllCount?: number;
  resourceLabel?: string;
  setPendingAction?: (action: PendingBulkAction | null) => void;
  setRowSelection: (selection: RowSelectionState) => void;
  setIsAllSelected?: (value: boolean) => void;
}) {
  // Select-all hands us no rows, so an action that only knows how to work from
  // a row list would fire on an empty one — which the backend reads as "no
  // narrowing", i.e. everything. Such actions stay out of the bar entirely.
  const canApplyToAll = (action: BulkAction<Data>) =>
    action.items
      ? action.items.some((item) => item.onClickAll)
      : !!action.onClickAll;

  const visibleActions = bulkActions.filter(
    (a) =>
      (!a.isHidden || !a.isHidden(selectedRows)) &&
      (!isAllSelected || canApplyToAll(a))
  );
  const iconOnly = visibleActions.length >= 4;
  const isAnyLoading = bulkActions.some((a) => a.isLoading);
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    if (!isAnyLoading && wasLoadingRef.current) {
      setRowSelection({});
      setIsAllSelected?.(false);
    }
    wasLoadingRef.current = isAnyLoading;
  }, [isAnyLoading, setRowSelection, setIsAllSelected]);

  const clearSelection = () => {
    setRowSelection({});
    setIsAllSelected?.(false);
  };

  const affectedCount = isAllSelected
    ? (selectAllCount ?? 0)
    : selectedRows.length;

  const runMenuItem = (
    action: BulkAction<Data>,
    item: BulkActionMenuItem<Data>
  ) => {
    if (isAllSelected && !item.onClickAll) return;
    if (isAllSelected && item.onClickAll) {
      item.onClickAll();
      clearSelection();
    } else {
      item.onClick(selectedRows);
      if (action.isLoading === undefined) clearSelection();
    }
  };

  const runAction = (action: BulkAction<Data>, destructive: boolean) => {
    if (isAllSelected && !action.onClickAll) return;

    const perform = () => {
      if (isAllSelected && action.onClickAll) {
        action.onClickAll();
        clearSelection();
      } else {
        action.onClick?.(selectedRows);
        if (action.isLoading === undefined) clearSelection();
      }
    };

    if (setPendingAction) {
      const confirmation = action.confirm?.(affectedCount, isAllSelected);
      if (confirmation) {
        setPendingAction({
          ...confirmation,
          confirmText: confirmation.confirmText ?? action.name,
          destructive,
          run: perform,
        });
        return;
      }

      // A destructive action on the whole filtered set still confirms, even
      // without its own copy: the rows it hits are not on screen to review.
      if (isAllSelected && action.onClickAll && destructive) {
        setPendingAction({
          title: `${action.name} all ${affectedCount} ${resourceLabel}s?`,
          description: `This will apply "${action.name}" to all ${affectedCount} ${resourceLabel}s matching the current filters. This cannot be undone.`,
          confirmText: action.name,
          destructive,
          run: perform,
        });
        return;
      }
    }

    perform();
  };

  return (
    <>
      {visibleActions.map((action, index) => {
        const destructive = !!action.color?.includes("destructive");
        const label = (
          <>
            {action.isLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <action.icon size="1rem" />
            )}
            {!iconOnly && action.name}
          </>
        );

        // Dropdown (grouped) action — e.g. the "Export" menu.
        if (action.items) {
          const trigger = (
            <DropdownMenuTrigger asChild>
              <button
                aria-label={action.name}
                data-testid="bulk-action"
                data-action={action.name}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:opacity-80 cursor-pointer"
              >
                {label}
                {!iconOnly && <ChevronDown className="size-3.5 opacity-60" />}
              </button>
            </DropdownMenuTrigger>
          );

          return (
            <DropdownMenu key={index}>
              {iconOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                  <TooltipContent>{action.name}</TooltipContent>
                </Tooltip>
              ) : (
                trigger
              )}
              <DropdownMenuContent
                side="top"
                align="end"
                sideOffset={20}
                alignOffset={-16}
                className="w-96"
              >
                {action.items
                  .filter((item) => !isAllSelected || item.onClickAll)
                  .map((item) => (
                    <DropdownMenuItem
                      key={item.name}
                      className="flex flex-col items-start gap-0.5"
                      onSelect={() => runMenuItem(action, item)}
                    >
                      <span className="text-sm font-medium">{item.name}</span>
                      {item.description && (
                        <span className="text-xs text-muted-foreground">
                          {item.description}
                        </span>
                      )}
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        const buttonContent = (
          <button
            key={index}
            data-testid="bulk-action"
            data-action={action.name}
            className={cn(
              "flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80 cursor-pointer",
              destructive ? "text-destructive" : "text-foreground"
            )}
            onClick={() => runAction(action, destructive)}
          >
            {label}
          </button>
        );

        if (iconOnly) {
          return (
            <Tooltip key={index}>
              <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
              <TooltipContent>{action.name}</TooltipContent>
            </Tooltip>
          );
        }

        return buttonContent;
      })}
    </>
  );
}
